/**
 * password-strength-analysis.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Component, Input } from "@angular/core";
import { FormControl } from "@angular/forms";
import "zxcvbn";

import { oneline } from "../support/utilities/functions";

/**
 * Ratio of progress bar fullness that a "success" result should be at.
 */
const PROGRESS_SUCCESS : number = 0.66;

/**
 * Which crack time estimate to use in the description. This *MUST* be one of
 * the keys of zxcvbn's crack_times_* dictionaries. (Since it's unlikely to
 * change, I haven't contorted the types to do a keyof compile check.)
 */
const CRACK_TIME_KEY : string = "offline_slow_hashing_1e4_per_second";

/**
 * The {@link PasswordStrengthAnalysisComponent} displays stats and text about
 * the strength of a user-entered password, for informing them of rudimentary
 * security concepts and explaining why we reject more passwords that "average".
 */
@Component({
    selector: "password-strength-analysis",
    template: `
        <div class="progress">
          <div
            class="progress-bar"
            [class.progress-bar-warning]="guessMagnitude < minMagnitude"
            [class.progress-bar-success]="guessMagnitude >= minMagnitude"
            role="progressbar"
            [attr.aria-valuenow]="guessMagnitude"
            style="min-width: 2em"
            aria-valuemin="0"
            aria-valuemax="16"
            [style.width]="barWidth">
            {{guessMagnitude}}
          </div>
        </div>
        <p>
          An estimate of your password's strength is shown above. The server
          requires a minimum strength of
          <strong>{{minMagnitude}}</strong>,
          indicating that your password would need at least
          <strong>10<sup>{{minMagnitude}}</sup></strong>
          guesses to break (on average).
        </p>
        <p>
          Under a typical
          <dfn [title]="attackDefinition">
            offline attack scenario
          </dfn>
          this password would take
          {{readableTimeEstimate}}
          to crack.
        </p>
        `
})
export class PasswordStrengthAnalysisComponent
{
    /**
     * Helper definition for terminology used in the template.
     */
    public readonly attackDefinition : string = oneline`
        A criticial situation where attackers have compromised the database and
        have `;

    /**
     * The minimum magnitude required. This doesn't actually enforce the rule,
     * but is used in the explanation (i.e. if using a form control, the field's
     * validator will need to be set up externally).
     */
    @Input()
    public minMagnitude : number;

    /**
     * The password analysis (by zxcvbn) to display. This input is used if the
     * tool is already being run externally.
     */
    @Input()
    public results : ZxcvbnResult;

    /**
     * TODO: It'd be nice if this component could auto-latch onto a formcontrol
     * and subscribe to value changes on its own. (Maybe we could even do some
     * validator injection?)
     */
    @Input()
    public set control (ctrl : FormControl)
    {
        throw new Error("Not yet supported!");
    }

    /**
     * Template helper to determine progress bar position for a given score.
     */
    public get barWidth () : string
    {
      return (
        Math.min(
          this.guessMagnitude / this.minMagnitude * PROGRESS_SUCCESS * 100,
          100
        ) + "%"
      );
    }

    /**
     * Shortcut to extract log_10(gueses required) from the results.
     */
    public get guessMagnitude () : number
    {
        return this.results ? Math.floor(this.results.guesses_log10) : 1;
    }

    /**
     * Shortcut to extract a readable time-to-crack estimate from the results.
     */
    public get readableTimeEstimate () : string
    {
        return (this.results
                ? this.results.crack_times_display[CRACK_TIME_KEY]
                : "no time");
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/password-strength-analysis.component.ts